import type { GetPage } from "skedyul-core/features/crm/features/pages/resources/types";
import {
  buildWorkplacePageHref,
  materializeInstancePath,
} from "skedyul-core/features/crm/features/pages/utils/pagePaths";

type PageDataLike = Pick<
  GetPage,
  "path" | "type" | "modelId" | "instancePagePath" | "breadcrumbs" | "models"
>;

export function resolveInstancePagePathFromPageData(
  pageData: PageDataLike | null | undefined,
  model?: { handlePlural?: string } | null,
): string {
  if (pageData?.instancePagePath) {
    return pageData.instancePagePath;
  }
  const handlePlural = model?.handlePlural ?? "records";
  return `${handlePlural}/[id]`;
}

export function resolveListPathFromContext(
  pageData: PageDataLike | null | undefined,
  fromSearchParam?: string | null,
): string | undefined {
  if (pageData?.type === "LIST" && pageData.path) {
    return pageData.path;
  }

  if (fromSearchParam) {
    return fromSearchParam;
  }

  const listBreadcrumb = pageData?.breadcrumbs?.find(
    (crumb) => crumb.type === "LIST",
  );
  return listBreadcrumb?.href || undefined;
}

export function buildListHref(
  subdomain: string,
  listPath: string,
  search?: URLSearchParams | Record<string, string | undefined>,
): string {
  return buildWorkplacePageHref({ subdomain, pagePath: listPath, search });
}

export function buildInstanceHref(props: {
  subdomain: string;
  instancePagePath: string;
  instanceId: string;
  fromListPath?: string | null;
  search?: Record<string, string | undefined> | null;
}): string {
  const materializedPath = materializeInstancePath(
    props.instancePagePath,
    props.instanceId,
  );
  const search = new URLSearchParams();
  if (props.fromListPath) {
    search.set("from", props.fromListPath);
  }
  if (props.search) {
    for (const [key, value] of Object.entries(props.search)) {
      if (value != null && value !== "") {
        search.set(key, value);
      }
    }
  }
  return buildWorkplacePageHref({
    subdomain: props.subdomain,
    pagePath: materializedPath,
    search: search.toString() ? search : undefined,
  });
}

export function buildNewInstanceHref(props: {
  subdomain: string;
  instancePagePath: string;
  fromListPath?: string | null;
}): string {
  return buildInstanceHref({
    subdomain: props.subdomain,
    instancePagePath: props.instancePagePath,
    instanceId: "new",
    fromListPath: props.fromListPath,
  });
}

export function buildInstanceHrefFromPageData(
  pageData: PageDataLike | null | undefined,
  props: {
    subdomain: string;
    instanceId: string;
    model?: { handlePlural?: string } | null;
    fromListPath?: string | null;
    search?: Record<string, string | undefined> | null;
  },
): string {
  const instancePagePath = resolveInstancePagePathFromPageData(
    pageData,
    props.model,
  );
  const fromListPath =
    props.fromListPath ??
    resolveListPathFromContext(pageData, undefined);

  return buildInstanceHref({
    subdomain: props.subdomain,
    instancePagePath,
    instanceId: props.instanceId,
    fromListPath,
    search: props.search,
  });
}

export function buildNewInstanceHrefFromPageData(
  pageData: PageDataLike | null | undefined,
  props: {
    subdomain: string;
    model?: { handlePlural?: string } | null;
    fromListPath?: string | null;
  },
): string {
  const instancePagePath = resolveInstancePagePathFromPageData(
    pageData,
    props.model,
  );
  const fromListPath =
    props.fromListPath ??
    resolveListPathFromContext(pageData, undefined);

  return buildNewInstanceHref({
    subdomain: props.subdomain,
    instancePagePath,
    fromListPath,
  });
}

export { buildWorkplacePageHref, materializeInstancePath };
